import { type PanelState, usePanelNavigation } from '../stores/usePanelNavigation';
import { usePanelScopeRegistry } from '../stores/usePanelScopeRegistry';
import { type SelectionData, useSelectionChannel } from '../stores/useSelectionChannel';

interface SplitPanelV2DevHarnessOptions {
  defaultPanelId?: string;
  panelResource?: string;
  scopeKey?: string;
}

interface SplitPanelV2DevHarness {
  closePanel: () => void;
  inspectState: () => {
    panel: PanelState | undefined;
    selection: SelectionData | null;
  };
  openEmbedded: (panelId?: string) => void;
  openStandalone: (panelId?: string) => void;
  panelResource: string;
  publishSelection: (panelId?: string) => void;
  registerScope: () => void;
  resetScope: () => void;
  scopeKey: string;
}

declare global {
  interface Window {
    __SPLIT_PANEL_V2_DEV__?: SplitPanelV2DevHarness;
  }
}

const DEFAULT_SCOPE_KEY = 'dns/zone';
const DEFAULT_PANEL_RESOURCE = 'zone';
const DEFAULT_PANEL_ID = 'zone-dev-1';

/**
 * Split Panel V2 수동 테스트용 dev harness 객체를 생성합니다.
 * @description
 * - scopeKey/panelResource 기본값을 설정하고, selection/panel 상태를 조작할 헬퍼를 만듭니다.
 */
const createDevHarness = (options: SplitPanelV2DevHarnessOptions): SplitPanelV2DevHarness => {
  const scopeKey = options.scopeKey ?? DEFAULT_SCOPE_KEY;
  const panelResource = options.panelResource ?? DEFAULT_PANEL_RESOURCE;
  const defaultPanelId = options.defaultPanelId ?? DEFAULT_PANEL_ID;

  const registryStore = usePanelScopeRegistry.getState();
  const selectionStore = useSelectionChannel.getState();
  const navigationStore = usePanelNavigation.getState();

  const registerScope = () => {
    const existing = registryStore.getScopeConfig(scopeKey);
    if (existing) {
      console.info(
        '[SplitPanelV2DevHarness] scope already registered',
        scopeKey,
        existing.panelResource,
      );
      return;
    }

    registryStore.registerScopes([
      {
        scopeKey,
        panelResource,
        bridgeMode: 'on',
        defaultPanelMode: 'panel',
      },
    ]);
    console.info('[SplitPanelV2DevHarness] scope registered', scopeKey, panelResource);
  };

  const publishSelection = (panelId = defaultPanelId) => {
    registerScope();
    selectionStore.publishSelection(scopeKey, {
      ids: [panelId],
      mode: 'single',
    });
    console.info('[SplitPanelV2DevHarness] publishSelection', { scopeKey, panelId });
  };

  const openStandalone = (panelId = defaultPanelId) => {
    registerScope();
    // Phase 6: openDetailPanel → openSidePanel 리네이밍
    navigationStore.openSidePanel({
      scopeKey,
      panelResource,
      panelId,
      viewMode: 'standalone',
    });
    console.info('[SplitPanelV2DevHarness] openStandalone', { scopeKey, panelResource, panelId });
  };

  const openEmbedded = (panelId = defaultPanelId) => {
    registerScope();
    // Phase 6: openDetailPanel → openSidePanel 리네이밍
    navigationStore.openSidePanel({
      scopeKey,
      panelResource,
      panelId,
      viewMode: 'embedded',
    });
    console.info('[SplitPanelV2DevHarness] openEmbedded (expect warn)', {
      scopeKey,
      panelResource,
      panelId,
    });
  };

  const closePanel = () => {
    navigationStore.closePanel(scopeKey);
    console.info('[SplitPanelV2DevHarness] closePanel', scopeKey);
  };

  const resetScope = () => {
    navigationStore.resetScope(scopeKey);
    selectionStore.clearSelection(scopeKey);
    console.info('[SplitPanelV2DevHarness] resetScope', scopeKey);
  };

  const inspectState = () => {
    const selection = selectionStore.getSelection(scopeKey);
    const panel = navigationStore.panels.get(scopeKey);
    const snapshot = { selection, panel };
    console.info('[SplitPanelV2DevHarness] inspectState', snapshot);
    return snapshot;
  };

  return {
    scopeKey,
    panelResource,
    registerScope,
    publishSelection,
    openStandalone,
    openEmbedded,
    closePanel,
    resetScope,
    inspectState,
  };
};

/**
 * Split Panel V2 수동 테스트를 위해 브라우저 콘솔에 dev harness를 부착합니다.
 * @description
 * - NODE_ENV가 production이 아니고 브라우저 환경일 때 `window.__SPLIT_PANEL_V2_DEV__`에 테스트 함수를 노출합니다.
 * - scope 등록 → selection 발행 → embedded 가드/dedup/close/reset 흐름을 콘솔에서 바로 실행할 수 있습니다.
 * @example
 * // 앱 진입부(dev 전용)에서 호출
 * attachSplitPanelV2DevHarness();
 * // 콘솔 예시
 * window.__SPLIT_PANEL_V2_DEV__?.publishSelection('zone-1');
 * window.__SPLIT_PANEL_V2_DEV__?.openEmbedded('zone-2'); // embedded 가드 warn 확인
 */
export const attachSplitPanelV2DevHarness = (
  options: SplitPanelV2DevHarnessOptions = {},
): SplitPanelV2DevHarness | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (process.env.NODE_ENV === 'production') {
    console.info('[SplitPanelV2DevHarness] skipped (production)');
    return null;
  }

  const harness = createDevHarness(options);
  window.__SPLIT_PANEL_V2_DEV__ = harness;

  console.info('[SplitPanelV2DevHarness] attached', {
    scopeKey: harness.scopeKey,
    panelResource: harness.panelResource,
  });
  console.info(
    '[SplitPanelV2DevHarness] 콘솔에서 사용 예: registerScope(), publishSelection(), openEmbedded(), openStandalone(), inspectState()',
  );

  return harness;
};
